import React, { useRef, useState } from 'react'
import { ApplicationData } from '../SubmitDealApplication'

interface FileUploadSectionProps {
  applicationData: ApplicationData
  updateApplicationData: (updates: Partial<ApplicationData>) => void
}

const FileUploadSection: React.FC<FileUploadSectionProps> = ({
  applicationData,
  updateApplicationData
}) => {
  const applicationFileInputRef = useRef<HTMLInputElement>(null)
  const bankStatementsInputRef = useRef<HTMLInputElement>(null)
  const [dragStates, setDragStates] = useState({
    applicationFile: false,
    bankStatements: false
  })

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const validateFileType = (file: File, allowedTypes: string[]): boolean => {
    return allowedTypes.includes(file.type)
  }

  const validateFileSize = (file: File, maxSizeMB: number = 10): boolean => {
    const maxSizeBytes = maxSizeMB * 1024 * 1024
    return file.size <= maxSizeBytes
  }

  const showFileError = (message: string) => {
    // You could replace this with a toast notification or proper error state
    alert(message)
  }

  const handleApplicationFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
    
    // Validate each file
    for (const file of files) {
      if (!validateFileType(file, allowedTypes)) {
        showFileError(`File "${file.name}" is not a valid type. Please upload PDF, DOC, or DOCX files.`)
        return
      }
      
      if (!validateFileSize(file)) {
        showFileError(`File "${file.name}" is too large. Each file must be less than 10MB.`)
        return
      }
    }

    // Check total file count
    const currentFiles = applicationData.applicationFiles
    const newFiles = [...currentFiles, ...files]
    
    if (newFiles.length > 3) {
      showFileError('Maximum 3 application files allowed.')
      return
    }
    
    // Update application data with the files - AI parsing will happen on Next click
    updateApplicationData({ applicationFiles: newFiles })
    
    // Clear the input
    if (applicationFileInputRef.current) {
      applicationFileInputRef.current.value = ''
    }
  }

  const handleBankStatementsUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    const allowedTypes = ['application/pdf', 'text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']
    
    // Validate each file
    for (const file of files) {
      if (!validateFileType(file, allowedTypes)) {
        showFileError(`File "${file.name}" is not a valid type. Please upload PDF, CSV, XLS, or XLSX files.`)
        return
      }
      
      if (!validateFileSize(file)) {
        showFileError(`File "${file.name}" is too large. Each file must be less than 10MB.`)
        return
      }
    }
    
    const currentFiles = applicationData.bankStatements
    const newFiles = [...currentFiles, ...files]
    updateApplicationData({ bankStatements: newFiles })
  }

  // Drag and Drop Handlers
  const handleDragOver = (e: React.DragEvent, uploadType: 'applicationFile' | 'bankStatements') => {
    e.preventDefault()
    e.stopPropagation()
    
    // Only show drag state if files are being dragged
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setDragStates(prev => ({ ...prev, [uploadType]: true }))
    }
  }

  const handleDragEnter = (e: React.DragEvent, uploadType: 'applicationFile' | 'bankStatements') => {
    e.preventDefault()
    e.stopPropagation()
    
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setDragStates(prev => ({ ...prev, [uploadType]: true }))
    }
  }

  const handleDragLeave = (e: React.DragEvent, uploadType: 'applicationFile' | 'bankStatements') => {
    e.preventDefault()
    e.stopPropagation()
    
    // Only reset drag state if we're actually leaving the drop zone
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    const x = e.clientX
    const y = e.clientY
    
    if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
      setDragStates(prev => ({ ...prev, [uploadType]: false }))
    }
  }

  const handleApplicationFileDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragStates(prev => ({ ...prev, applicationFile: false }))

    const files = Array.from(e.dataTransfer.files)
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
    
    // Validate each file
    for (const file of files) {
      if (!validateFileType(file, allowedTypes)) {
        showFileError(`File "${file.name}" is not a valid type. Please upload PDF, DOC, or DOCX files.`)
        return
      }
      
      if (!validateFileSize(file)) {
        showFileError(`File "${file.name}" is too large. Each file must be less than 10MB.`)
        return
      }
    }

    // Check total file count
    const currentFiles = applicationData.applicationFiles
    const newFiles = [...currentFiles, ...files]
    
    if (newFiles.length > 3) {
      showFileError('Maximum 3 application files allowed.')
      return
    }
    
    // Update application data with the files - AI parsing will happen on Next click
    updateApplicationData({ applicationFiles: newFiles })
  }

  const handleBankStatementsDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragStates(prev => ({ ...prev, bankStatements: false }))

    const files = Array.from(e.dataTransfer.files)
    const allowedTypes = ['application/pdf', 'text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']
    
    // Validate each file
    for (const file of files) {
      if (!validateFileType(file, allowedTypes)) {
        showFileError(`File "${file.name}" is not a valid type. Please upload PDF, CSV, XLS, or XLSX files.`)
        return
      }
      
      if (!validateFileSize(file)) {
        showFileError(`File "${file.name}" is too large. Each file must be less than 10MB.`)
        return
      }
    }
    
    const currentFiles = applicationData.bankStatements
    const newFiles = [...currentFiles, ...files]
    updateApplicationData({ bankStatements: newFiles })
  }

  const removeApplicationFile = (index: number) => {
    const newFiles = applicationData.applicationFiles.filter((_, i) => i !== index)
    updateApplicationData({ applicationFiles: newFiles })
  }

  const removeBankStatement = (index: number) => {
    const newFiles = applicationData.bankStatements.filter((_, i) => i !== index)
    updateApplicationData({ bankStatements: newFiles })
  }

  const triggerApplicationFileUpload = () => {
    applicationFileInputRef.current?.click()
  }

  const triggerBankStatementsUpload = () => {
    bankStatementsInputRef.current?.click()
  }

  return (
    <div className="submit-deal-form-section">
      {/* Spacer to align with sidebar header */}
      <div className="clean-form-spacer"></div>
      
      <div className="clean-form-layout">
        <div className="clean-form-sidebar">
          <h2 className="clean-form-sidebar-title">
            Document Upload
          </h2>
          <p className="clean-form-sidebar-description">
            Please upload your completed application form and the most recent 4 months of bank statements. 
            All documents should be in PDF format for faster processing.
          </p>
        </div>
        
        <div className="clean-form-container">
          <div className="clean-form-grid">

            {/* Application File Upload */}
            <div className="submit-deal-form-group clean-form-field-full">
              <label className="submit-deal-form-label required">
                Application Documents (1-3 files)
              </label>
          <div 
            className={`submit-deal-file-upload ${dragStates.applicationFile ? 'dragover' : ''}`}
            onClick={triggerApplicationFileUpload}
            onDragEnter={(e) => handleDragEnter(e, 'applicationFile')}
            onDragOver={(e) => handleDragOver(e, 'applicationFile')}
            onDragLeave={(e) => handleDragLeave(e, 'applicationFile')}
            onDrop={handleApplicationFileDrop}
          >
            <div className="submit-deal-file-upload-icon">
              <i className="bi bi-file-earmark-pdf"></i>
            </div>
            <div className="submit-deal-file-upload-text">
              {dragStates.applicationFile 
                ? 'Drop your application documents here'
                : applicationData.applicationFiles.length > 0 
                ? `Add More Application Documents (${applicationData.applicationFiles.length}/3)` 
                : 'Upload Application Documents'}
            </div>
            <div className="submit-deal-file-upload-hint">
              {dragStates.applicationFile 
                ? 'Release to upload files'
                : 'Click to browse or drag and drop your completed application forms (PDF recommended, up to 3 files)'}
            </div>
          </div>
          <input
            ref={applicationFileInputRef}
            type="file"
            className="submit-deal-file-input"
            accept=".pdf,.doc,.docx"
            multiple
            onChange={handleApplicationFileUpload}
          />
          
          {applicationData.applicationFiles.length > 0 && (
            <div className="submit-deal-file-list">
              {applicationData.applicationFiles.map((file, index) => (
                <div key={index} className="submit-deal-file-item">
                  <div className="submit-deal-file-info">
                    <i className="bi bi-file-earmark-pdf submit-deal-file-icon"></i>
                    <div>
                      <div className="submit-deal-file-name">{file.name}</div>
                      <div className="submit-deal-file-size">{formatFileSize(file.size)}</div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="submit-deal-file-remove"
                    onClick={() => removeApplicationFile(index)}
                    title="Remove file"
                  >
                    <i className="bi bi-x-lg">×</i>
                  </button>
                </div>
              ))}
            </div>
          )}
              <div className="submit-deal-form-help">
                Upload your completed funding application forms. Accepted formats: PDF, DOC, DOCX (Max 10MB each, up to 3 files)
              </div>
            </div>

            {/* Bank Statements Upload */}
            <div className="submit-deal-form-group clean-form-field-full">
              <label className="submit-deal-form-label required">
                Bank Statements (Most Recent 3+ Months)
              </label>
          <div 
            className={`submit-deal-file-upload ${dragStates.bankStatements ? 'dragover' : ''}`}
            onClick={triggerBankStatementsUpload}
            onDragEnter={(e) => handleDragEnter(e, 'bankStatements')}
            onDragOver={(e) => handleDragOver(e, 'bankStatements')}
            onDragLeave={(e) => handleDragLeave(e, 'bankStatements')}
            onDrop={handleBankStatementsDrop}
          >
            <div className="submit-deal-file-upload-icon">
              <i className="bi bi-file-earmark-spreadsheet"></i>
            </div>
            <div className="submit-deal-file-upload-text">
              {dragStates.bankStatements 
                ? 'Drop your bank statements here'
                : applicationData.bankStatements.length > 0 
                ? 'Add More Bank Statements' 
                : 'Upload Bank Statements'}
            </div>
            <div className="submit-deal-file-upload-hint">
              {dragStates.bankStatements 
                ? 'Release to upload files'
                : 'Click to browse or drag and drop your bank statements (minimum 3 months required)'}
            </div>
          </div>
          <input
            ref={bankStatementsInputRef}
            type="file"
            className="submit-deal-file-input"
            accept=".pdf,.csv,.xlsx,.xls"
            multiple
            onChange={handleBankStatementsUpload}
          />
          
          {applicationData.bankStatements.length > 0 && (
            <div className="submit-deal-file-list">
              {applicationData.bankStatements.map((file, index) => (
                <div key={index} className="submit-deal-file-item">
                  <div className="submit-deal-file-info">
                    <i className="bi bi-file-earmark-spreadsheet submit-deal-file-icon"></i>
                    <div>
                      <div className="submit-deal-file-name">{file.name}</div>
                      <div className="submit-deal-file-size">{formatFileSize(file.size)}</div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="submit-deal-file-remove"
                    onClick={() => removeBankStatement(index)}
                    title="Remove file"
                  >
                    <i className="bi bi-x-lg">×</i>
                  </button>
                </div>
              ))}
            </div>
          )}
              <div className="submit-deal-form-help">
                Upload bank statements for the most recent 3+ months. Accepted formats: PDF, CSV, XLSX, XLS (Max 10MB each)
                <br />
                <strong>Files uploaded: {applicationData.bankStatements.length}/3 minimum required</strong>
              </div>
            </div>

            {/* Upload Progress Summary */}
            <div className="submit-deal-form-group clean-form-field-full">
              <div 
                className="upload-summary-container"
                style={{
                  backgroundColor: '#f1f3f4',
                  borderRadius: '12px',
                  padding: '20px',
                  border: '1px solid #e5e7eb'
                }}
              >
                <h6 
                  className="upload-summary-heading"
                  style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#02225b',
                    marginBottom: '16px',
                    textAlign: 'center'
                  }}
                >
                  Upload Summary
                </h6>
                
                <div className="upload-summary-items">
                  <div 
                    className="upload-summary-item"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 0',
                      borderBottom: '1px solid #e5e7eb'
                    }}
                  >
                    <span 
                      style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#02225b'
                      }}
                    >
                      Application Documents:
                    </span>
                    {applicationData.applicationFiles.length > 0 ? (
                      <span className="text-success" style={{ fontSize: '14px', fontWeight: '500' }}>
                        <i className="bi bi-check-circle me-1"></i>
                        {applicationData.applicationFiles.length} file{applicationData.applicationFiles.length > 1 ? 's' : ''} uploaded
                      </span>
                    ) : (
                      <span className="text-warning" style={{ fontSize: '14px', fontWeight: '500' }}>
                        <i className="bi bi-exclamation-circle me-1"></i>
                        Required
                      </span>
                    )}
                  </div>
                  
                  <div 
                    className="upload-summary-item"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 0'
                    }}
                  >
                    <span 
                      style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#02225b'
                      }}
                    >
                      Bank Statements:
                    </span>
                    {applicationData.bankStatements.length >= 3 ? (
                      <span className="text-success" style={{ fontSize: '14px', fontWeight: '500' }}>
                        <i className="bi bi-check-circle me-1"></i>
                        {applicationData.bankStatements.length} files uploaded
                      </span>
                    ) : (
                      <span className="text-warning" style={{ fontSize: '14px', fontWeight: '500' }}>
                        <i className="bi bi-exclamation-circle me-1"></i>
                        {applicationData.bankStatements.length}/3 minimum required
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FileUploadSection
